/**
 * Settings panel — slide-out drawer from the top-right.
 *
 * Opened via the cog-wheel button in the app header. Sections:
 *   · Account         — sign-in identity (synced to the identity
 *                       provider via /viewer-api/me/*).  Edits land
 *                       on the user's account record.
 *   · Profile         — reader's displayed name, credentials, title
 *                       (saved locally for PDF rendering only).
 *   · Organisation    — org name + department + contact + logo
 *   · Language        — UI locale (EN / VI); used to live as a
 *                       separate top-bar switcher, now centralised here
 *   · Preferences     — small toggles (more to come)
 *
 * Account-section fields round-trip to the identity provider; the
 * other sections still write to `vpd.profile.v1` in localStorage so
 * the PDF export sees the live values on the next print.  Logo +
 * signature inputs accept file-picked images and base64-encode them
 * so they survive localStorage round-trip.
 */
import { createEffect, createSignal, For, Show } from "solid-js";
import { profile, updateProfile, type Language } from "./profile";
import { locale, setLocale, type Locale } from "../i18n";
import { patchClaims } from "../auth/store";

interface AccountState {
  userId:        string;
  userName:      string;
  firstName:     string;
  lastName:      string;
  displayName:   string;
  email:         string;
  emailVerified: boolean;
}

function authHeader(): string {
  const a = JSON.parse(sessionStorage.getItem("vpd.auth.v1") ?? "null");
  return `Bearer ${a?.accessToken ?? ""}`;
}

export function SettingsPanel(p: { open: boolean; onClose: () => void }) {
  const [section, setSection] = createSignal<"account" | "profile" | "org" | "prefs">("account");

  // ── Account-section state ─────────────────────────────────────
  // Loaded on first open and refreshed after every successful save
  // so the displayed value matches the server even if validation
  // rewrote what we sent.
  const [acct,     setAcct]     = createSignal<AccountState | null>(null);
  const [acctErr,  setAcctErr]  = createSignal<string | null>(null);
  const [acctNote, setAcctNote] = createSignal<string | null>(null);
  const [acctBusy, setAcctBusy] = createSignal<string | null>(null);
  const [acctFirst,   setAcctFirst]   = createSignal("");
  const [acctLast,    setAcctLast]    = createSignal("");
  const [acctDisplay, setAcctDisplay] = createSignal("");
  const [acctUser,    setAcctUser]    = createSignal("");
  const [acctEmail,   setAcctEmail]   = createSignal("");

  async function loadAccount() {
    setAcctErr(null);
    try {
      const rsp = await fetch("/viewer-api/me", { headers: { Authorization: authHeader() } });
      if (!rsp.ok) throw new Error(`HTTP ${rsp.status} — ${await rsp.text()}`);
      const j = await rsp.json() as AccountState;
      setAcct(j);
      setAcctFirst(j.firstName);
      setAcctLast(j.lastName);
      setAcctDisplay(j.displayName);
      setAcctUser(j.userName);
      setAcctEmail(j.email);
    } catch (e) { setAcctErr((e as Error).message); }
  }
  // Fetch the account record only after the panel actually opens —
  // the SettingsPanel component is mounted at App startup (before
  // sign-in), so onMount-based fetching would 401.
  createEffect(() => {
    if (p.open && !acct() && !acctErr()) loadAccount();
  });

  // Single-button save: PUT only the sections whose values
  // actually changed since the last load.  Without the dirty-check
  // Zitadel would reject same-value PUTs (COMMAND-2b7fM
  // "Email not changed") — the helpers also catch that upstream
  // signal as a benign no-op, so dirty-check is the cheap
  // optimisation, fail-closed isn't needed.
  async function saveAcctAll(ev: Event) {
    ev.preventDefault();
    setAcctBusy("save"); setAcctNote(null); setAcctErr(null);
    const cur = acct();
    if (!cur) { setAcctBusy(null); return; }

    const changed: Array<{ label: string; run: () => Promise<string> }> = [];

    if (acctFirst()   !== cur.firstName ||
        acctLast()    !== cur.lastName  ||
        acctDisplay() !== cur.displayName) {
      changed.push({
        label: "name",
        run: async () => {
          const rsp = await fetch("/viewer-api/me/profile", {
            method: "PUT",
            headers: { "content-type": "application/json", Authorization: authHeader() },
            body: JSON.stringify({ firstName: acctFirst(), lastName: acctLast(), displayName: acctDisplay() }),
          });
          if (!rsp.ok) throw new Error(`name: HTTP ${rsp.status} — ${await rsp.text()}`);
          return "name";
        },
      });
    }
    if (acctUser() !== cur.userName) {
      changed.push({
        label: "username",
        run: async () => {
          const rsp = await fetch("/viewer-api/me/username", {
            method: "POST",
            headers: { "content-type": "application/json", Authorization: authHeader() },
            body: JSON.stringify({ userName: acctUser() }),
          });
          if (!rsp.ok) throw new Error(`username: HTTP ${rsp.status} — ${await rsp.text()}`);
          return "username — use the new value on next sign-in";
        },
      });
    }
    if (acctEmail() !== cur.email) {
      changed.push({
        label: "email",
        run: async () => {
          const rsp = await fetch("/viewer-api/me/email", {
            method: "POST",
            headers: { "content-type": "application/json", Authorization: authHeader() },
            body: JSON.stringify({ email: acctEmail() }),
          });
          if (!rsp.ok) throw new Error(`email: HTTP ${rsp.status} — ${await rsp.text()}`);
          const j = await rsp.json();
          return j.message ?? "email — verification sent to the new address";
        },
      });
    }

    if (changed.length === 0) {
      setAcctNote("No changes to save.");
      setAcctBusy(null);
      return;
    }

    const results = await Promise.allSettled(changed.map((c) => c.run()));
    const okMsgs: string[] = [];
    const errMsgs: string[] = [];
    results.forEach((r) => {
      if (r.status === "fulfilled") okMsgs.push(r.value);
      else errMsgs.push(r.reason instanceof Error ? r.reason.message : String(r.reason));
    });
    if (errMsgs.length) setAcctErr(errMsgs.join(" · "));
    if (okMsgs.length)  setAcctNote("Saved: " + okMsgs.join(" · "));
    await loadAccount();
    // Mirror the new values into the in-memory auth claims so the
    // user-name chip + every other auth().claims consumer updates
    // immediately.  The JWT itself stays the same until the next
    // refresh / sign-in; this is a cosmetic patch.
    const fresh = acct();
    if (fresh) {
      patchClaims({
        name:               fresh.displayName || `${fresh.firstName} ${fresh.lastName}`.trim(),
        preferred_username: fresh.userName,
        email:              fresh.email,
      });
    }
    setAcctBusy(null);
  }

  async function resendAcctVerify() {
    setAcctBusy("verify"); setAcctNote(null); setAcctErr(null);
    try {
      const rsp = await fetch("/viewer-api/me/email/resend-verification", {
        method: "POST", headers: { Authorization: authHeader() },
      });
      if (!rsp.ok) throw new Error(`HTTP ${rsp.status} — ${await rsp.text()}`);
      setAcctNote("Verification email re-sent.");
    } catch (e) { setAcctErr((e as Error).message); }
    finally { setAcctBusy(null); }
  }

  // File → base64 helper for signature + logo inputs.
  async function readDataUrl(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const r = new FileReader();
      r.onload = () => resolve(String(r.result ?? ""));
      r.onerror = () => reject(r.error);
      r.readAsDataURL(file);
    });
  }

  return (
    <Show when={p.open}>
      {/* Scrim — click to close, same convention as the tray drawers. */}
      <div class="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm"
           onClick={p.onClose} />
      {/* Panel — slides in from the right. Max-w so wide monitors
          don't stretch the form into an unreadable single-line row. */}
      <aside class="fixed inset-y-0 right-0 z-50 flex w-[92%] max-w-[560px] flex-col border-l border-ink-300 bg-ink-100 shadow-xl">
        <div class="flex items-center gap-3 border-b border-ink-300 px-4 py-3">
          <span class="chip-brand">Settings</span>
          <span class="ml-auto text-[11px] text-ink-700">saved locally to this device</span>
          <button type="button"
                  class="btn-ghost !py-1 !px-2 text-[11px]"
                  onClick={p.onClose}>Close</button>
        </div>
        {/* Section tabs */}
        <div class="flex border-b border-ink-300">
          <SecTab active={section() === "account"} onClick={() => setSection("account")}>Account</SecTab>
          <SecTab active={section() === "profile"} onClick={() => setSection("profile")}>Profile</SecTab>
          <SecTab active={section() === "org"}     onClick={() => setSection("org")}>Organisation</SecTab>
          <SecTab active={section() === "prefs"}   onClick={() => setSection("prefs")}>Preferences</SecTab>
        </div>
        <div class="flex-1 overflow-auto p-4 text-[12px]">
          <Show when={section() === "account"}>
            <Show when={acctErr()}>
              <div class="mb-3 rounded border border-danger bg-danger/10 p-2 text-[11px] text-danger">{acctErr()}</div>
            </Show>
            <Show when={acctNote()}>
              <div class="mb-3 rounded border border-ok bg-ok/10 p-2 text-[11px] text-ok">{acctNote()}</div>
            </Show>
            <p class="mb-3 text-[11px] italic text-ink-700">
              These fields sync to your sign-in account. Edits land on your
              identity record and apply across every browser you sign in from.
            </p>

            <Show when={acct()} fallback={<p class="text-[11px] text-ink-700">loading…</p>}>
              {/* One form, three sections, one Save button at the
                  bottom.  The save handler dirty-checks per-section
                  and only PUTs the fields the user actually changed,
                  so an unedited Recovery email doesn't trigger
                  "Email not changed" upstream. */}
              <form onSubmit={saveAcctAll}>
                {/* — Name — */}
                <div class="grid grid-cols-2 gap-3">
                  <Row label="First name">
                    <input type="text" class={inputCls}
                           value={acctFirst()}
                           onInput={(e) => setAcctFirst(e.currentTarget.value)} />
                  </Row>
                  <Row label="Last name">
                    <input type="text" class={inputCls}
                           value={acctLast()}
                           onInput={(e) => setAcctLast(e.currentTarget.value)} />
                  </Row>
                </div>
                <Row label="Display name"
                     hint="Optional. Falls back to First Last when blank.">
                  <input type="text" class={inputCls}
                         value={acctDisplay()}
                         onInput={(e) => setAcctDisplay(e.currentTarget.value)} />
                </Row>

                {/* — Username — */}
                <div class="mt-4 border-t border-ink-300 pt-4">
                  <Row label="Sign-in username"
                       hint="3–64 chars; letters, digits and . _ @ + - only. Existing sessions stay valid; new value applies on next sign-in.">
                    <input type="text" class={inputCls}
                           value={acctUser()}
                           onInput={(e) => setAcctUser(e.currentTarget.value)} />
                  </Row>
                </div>

                {/* — Recovery email — */}
                <div class="mt-4 border-t border-ink-300 pt-4">
                  <div class="mb-2 flex items-center gap-2">
                    <Show when={acct()!.emailVerified}>
                      <span class="rounded-full bg-ok/15 px-2 py-0.5 text-[10px] uppercase tracking-wider text-ok ring-1 ring-ok">verified</span>
                    </Show>
                    <Show when={!acct()!.emailVerified}>
                      <span class="rounded-full bg-warn/15 px-2 py-0.5 text-[10px] uppercase tracking-wider text-warn ring-1 ring-warn">unverified</span>
                    </Show>
                    <span class="font-mono text-[11px] text-ink-700">{acct()!.email}</span>
                    <Show when={!acct()!.emailVerified}>
                      <button class="ml-auto btn-ghost !py-0.5 !px-1.5 text-[10px]" type="button"
                              disabled={acctBusy() === "verify"}
                              onClick={resendAcctVerify}>
                        {acctBusy() === "verify" ? "Sending…" : "Re-send verification"}
                      </button>
                    </Show>
                  </div>
                  <Row label="Recovery email"
                       hint="Where password-reset and security alerts go. Changing it sends a verification email to the new address; the previous address keeps receiving recovery messages until the new one is verified.">
                    <input type="email" class={inputCls}
                           placeholder="you@example.com"
                           value={acctEmail()}
                           onInput={(e) => setAcctEmail(e.currentTarget.value)} />
                  </Row>
                </div>

                {/* Single Save button at the bottom — applies whichever
                    of the three sections the user actually edited. */}
                <div class="mt-5 border-t border-ink-300 pt-4">
                  <button type="submit" class="btn-primary text-[11px]"
                          disabled={acctBusy() === "save"}>
                    {acctBusy() === "save" ? "Saving…" : "Save changes"}
                  </button>
                </div>
              </form>
            </Show>
          </Show>

          <Show when={section() === "profile"}>
            <Row label="Display name"
                 hint="Renders as the report signer when Preferences → override signer is enabled.">
              <input type="text" class={inputCls}
                     placeholder="Dr Jane Doe"
                     value={profile().displayName}
                     onInput={(e) => updateProfile({ displayName: e.currentTarget.value })} />
            </Row>
            <Row label="Credentials" hint='e.g. "MD", "MD, PhD", "MBBS, FRCPath"'>
              <input type="text" class={inputCls}
                     placeholder="MD, FRCPath"
                     value={profile().credentials}
                     onInput={(e) => updateProfile({ credentials: e.currentTarget.value })} />
            </Row>
            <Row label="Title / role"
                 hint="Printed below the signature on the report.">
              <input type="text" class={inputCls}
                     placeholder="Consultant Histopathologist"
                     value={profile().title}
                     onInput={(e) => updateProfile({ title: e.currentTarget.value })} />
            </Row>
            <Row label="Scanned signature"
                 hint="Optional — image of your handwritten signature. PNG / JPG.">
              <div class="flex items-center gap-2">
                <Show when={profile().signatureDataUrl}>
                  <img src={profile().signatureDataUrl}
                       class="max-h-16 rounded border border-ink-300 bg-white p-1" />
                </Show>
                <input type="file" accept="image/*"
                       class="text-[11px]"
                       onChange={async (e) => {
                         const f = e.currentTarget.files?.[0];
                         if (f) updateProfile({ signatureDataUrl: await readDataUrl(f) });
                       }} />
                <Show when={profile().signatureDataUrl}>
                  <button type="button"
                          class="ml-auto btn-ghost !py-0.5 !px-1.5 text-[10px]"
                          onClick={() => updateProfile({ signatureDataUrl: undefined })}>
                    remove
                  </button>
                </Show>
              </div>
            </Row>
          </Show>

          <Show when={section() === "org"}>
            <Row label="Organisation name">
              <input type="text" class={inputCls}
                     placeholder="Example Hospital"
                     value={profile().orgName}
                     onInput={(e) => updateProfile({ orgName: e.currentTarget.value })} />
            </Row>
            <Row label="Department">
              <input type="text" class={inputCls}
                     placeholder="Department of Pathology"
                     value={profile().orgDepartment}
                     onInput={(e) => updateProfile({ orgDepartment: e.currentTarget.value })} />
            </Row>
            <Row label="Address">
              <input type="text" class={inputCls}
                     placeholder="1234 Example St, City, ZIP"
                     value={profile().orgAddress}
                     onInput={(e) => updateProfile({ orgAddress: e.currentTarget.value })} />
            </Row>
            <div class="grid grid-cols-2 gap-3">
              <Row label="Phone">
                <input type="text" class={inputCls}
                       placeholder="+84 (28) 1234 5678"
                       value={profile().orgPhone}
                       onInput={(e) => updateProfile({ orgPhone: e.currentTarget.value })} />
              </Row>
              <Row label="Email">
                <input type="email" class={inputCls}
                       placeholder="pathology@example.org"
                       value={profile().orgEmail}
                       onInput={(e) => updateProfile({ orgEmail: e.currentTarget.value })} />
              </Row>
            </div>
            <Row label="Logo"
                 hint="Renders top-left of every PDF page. PNG or SVG, transparent background recommended.">
              <div class="flex items-center gap-2">
                <Show when={profile().orgLogoDataUrl}>
                  <img src={profile().orgLogoDataUrl}
                       class="max-h-16 max-w-32 rounded border border-ink-300 bg-white p-1" />
                </Show>
                <input type="file" accept="image/*"
                       class="text-[11px]"
                       onChange={async (e) => {
                         const f = e.currentTarget.files?.[0];
                         if (f) updateProfile({ orgLogoDataUrl: await readDataUrl(f) });
                       }} />
                <Show when={profile().orgLogoDataUrl}>
                  <button type="button"
                          class="ml-auto btn-ghost !py-0.5 !px-1.5 text-[10px]"
                          onClick={() => updateProfile({ orgLogoDataUrl: undefined })}>
                    remove
                  </button>
                </Show>
              </div>
            </Row>
          </Show>

          <Show when={section() === "prefs"}>
            <Row label="Language"
                 hint="Interface language. Applies immediately.">
              <div class="inline-flex rounded-md border border-ink-300" role="group">
                <For each={[{ v: "en" as Locale, label: "English" }, { v: "vi" as Locale, label: "Tiếng Việt" }]}>{(o) => (
                  <button type="button"
                          class="px-3 py-1 text-[11px]"
                          classList={{
                            "bg-brand text-white": locale() === o.v,
                            "text-ink-700 hover:text-white": locale() !== o.v,
                          }}
                          onClick={() => {
                            setLocale(o.v);
                            updateProfile({ language: o.v as Language });
                          }}>
                    {o.label}
                  </button>
                )}</For>
              </div>
            </Row>
            <Row label="Override signer"
                 hint="Use Profile → Display name + Credentials as the report signer, even if a different login is rendering the PDF.">
              <label class="inline-flex items-center gap-2 text-[12px] text-ink-900">
                <input type="checkbox"
                       checked={profile().overrideSignerFromProfile}
                       onChange={(e) => updateProfile({ overrideSignerFromProfile: e.currentTarget.checked })} />
                <span>Enable override</span>
              </label>
            </Row>
            {/* Placeholder for future: printer selection, audit-export
                cadence, default slide magnification, etc. */}
            <p class="mt-4 text-[11px] italic text-ink-700">
              More preferences (default printer, audit export cadence,
              default slide magnification) will land here in a follow-up.
            </p>
          </Show>
        </div>
      </aside>
    </Show>
  );
}

const inputCls = "w-full rounded border border-ink-300 bg-ink-50 px-2 py-1 text-[12px] text-ink-900 focus:border-brand focus:outline-none";

function Row(p: { label: string; hint?: string; children: any }) {
  return (
    <div class="mb-3">
      <label class="mb-0.5 block text-[10px] uppercase tracking-wider text-ink-700">
        {p.label}
      </label>
      {p.children}
      <Show when={p.hint}>
        <div class="mt-0.5 text-[10px] italic text-ink-700">{p.hint}</div>
      </Show>
    </div>
  );
}

function SecTab(p: { active: boolean; onClick: () => void; children: any }) {
  return (
    <button type="button"
            class="flex-1 border-b-2 px-3 py-2 text-center text-[11px] uppercase tracking-wider transition"
            classList={{
              "border-brand text-white": p.active,
              "border-transparent text-ink-700 hover:text-white": !p.active,
            }}
            onClick={p.onClick}>
      {p.children}
    </button>
  );
}
